import * as React from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid2';
import { useSearchParams } from 'react-router-dom';

import type { Metadata } from '../../../types/metadata';
import { config } from '../../../config';
import { dayjs } from '../../../lib/dayjs';
import { ItemsFilters } from './items-filters';
import type { Filters } from './items-filters';
import { ItemsPagination } from './items-pagination';
import { Stats } from './stats';
import { StorageProvider } from './storage-context';
import { StorageView } from './storage-view';
import type { Item } from './types';
import { UplaodButton } from './upload-button';
import { clientConfig, SetS3Config } from '../../../awsClientConfig';
import { userAccountData, userProfileData } from '../../../Common/profileInfo';
import { Auth, Storage } from 'aws-amplify';
import { getExtension } from './file-storage-utils';
import Loading from '../../../Common/Loading';
import { GetLatestFormsByProfile } from '../../../queries/GetLatestFormsByProfile';
import { FeatureFlags } from "../../../types/featureFlags";
import { useFlags } from "launchdarkly-react-client-sdk";
import Footer from '../../footer';

const metadata = { title: `File storage | Dashboard | ${config.site.name}` } satisfies Metadata;

export function FileStorage(): React.JSX.Element {
  const { query, sortDir, view = 'grid' } = useExtractSearchParams();
  const [loading, setLoading] = React.useState(false);

  const filters = { query };

  
  const [images, setImages] = React.useState<Item[]>([]);
  const [forms, setForms] = React.useState<Item[]>([]);
  const sortedItems = applySort([...images, ...forms], sortDir);
  const filteredItems = applyFilters(sortedItems, filters);
  const client = new clientConfig();
  const [page, SetPage] = React.useState(0)
  const flagList: FeatureFlags = useFlags();

  React.useEffect(() => {
    fetchApi();
    loadImages();
  },[]);

  React.useEffect(() => {
    const originalTitle = document.title;
    document.title = metadata.title;
    
    return () => {
      document.title = originalTitle;
    };
  }, []);

  const fetchApi = async () => {
    await Auth.currentAuthenticatedUser({ bypassCache: true });
    const partnerId = await userProfileData();
    await client
      .query({
        query: GetLatestFormsByProfile,
        variables: {
          ProfileId: partnerId,
        },
      })
      .then((res: any) => {
        let userForms = res.data.GetLatestFormsByProfile;
        userForms = userForms.map((form:any) => ({...form, UpdatedAt: new Date(form.UpdatedAt)}));
        setForms(userForms);
      });
  };

  const loadImages = async () => {
    setLoading(true);
    SetS3Config(
      `documents-${process.env.REACT_APP_MODE}-optima-financial-group`,
      "public"
    );

    const profileId = await userProfileData();
    const accountId = await userAccountData();
    try {
      await Storage.list(
        `Accounts/${accountId}/Profiles/${profileId}/Identifications/`,
        {
          // contentType: "image/png", // contentType is optional
          customPrefix: { public: "" },
          level: "public",
          // tagging: "ExternalClearance=Profile",
        }
      ).then(async (res) => {
        await getImages(res.results);
        setLoading(false);
      });
    } catch (error) {
      console.log("Error uploading file: ", error);
      setLoading(false);
    }
  };

  const getImages = async (url: any) => {
    SetS3Config(
      `documents-${process.env.REACT_APP_MODE}-optima-financial-group`,
      "public"
    );
    let getAllImages: any = [];
    url.forEach((element: any) => {
      getAllImages.push(loadSingleImage(element));
    });
    Promise.all(getAllImages).then((values: any) => {
      // const merged: any = [...forms, ...values];
      // setForms(merged);
      setImages(values);
    });
  };

  const loadSingleImage = async (url: any) => {
    const ispdf = isPdf(url.key);
    let contentType = ispdf ? "application/pdf" : "image/png";
    const imageData: string = await Storage.get(url.key, {
      level: "public",
      contentType: contentType,
      customPrefix: { public: "" },
    });
    let imageObj = {
      S3Bucket: imageData,
      Name: url.key.split("/")[5],
      S3Key: url.key,
      UpdatedAt: url.lastModified,
    };

    return imageObj;
  };

  function isPdf(filename: any) {
    var ext = getExtension(filename);
    switch (ext.toLowerCase()) {
      case "pdf":
        return true;
    }
    return false;
  }

  return (
    <>
    <div className="page-container">
      
      <Box
        sx={{
          maxWidth: 'var(--Content-maxWidth)',
          m: 'var(--Content-margin)',
          p: 'var(--Content-padding)',
          width: 'var(--Content-width)',
        }}
      >
        {loading? <Loading /> : 
        <Stack spacing={4}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} sx={{ alignItems: 'flex-start' }}>
            <Box sx={{ flex: '1 1 auto' }}>
              <Typography variant="h4">File Manager</Typography>
            </Box>
            {flagList?.featureProfileDocumentUpload &&
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <UplaodButton onUploadCompleted={loadImages}/>
              </Box>
            }
          </Stack>
          <Grid container spacing={4}>
            <Grid size={{xs:12,md:12}}>
              <Stack spacing={4}>
                <ItemsFilters filters={filters} sortDir={sortDir} view={view} />
                <StorageProvider items={filteredItems}>
                  <StorageView view={view} />
                </StorageProvider>
                <ItemsPagination count={filteredItems.length} page={page} onPageChnage={SetPage} />
              </Stack>
            </Grid>
          </Grid>
        </Stack>
        }
      </Box>   
    </div>
    <Footer/>
    </>
  );
}

function useExtractSearchParams(): { query?: string; sortDir?: 'asc' | 'desc'; view?: 'grid' | 'list' } {
  const [searchParams] = useSearchParams();

  return {
    query: searchParams.get('query') || undefined,
    sortDir: (searchParams.get('sortDir') || undefined) as 'asc' | 'desc' | undefined,
    view: (searchParams.get('view') || undefined) as 'grid' | 'list' | undefined,
  };
}

// Sorting and filtering has to be done on the server.

function applySort(row: Item[], sortDir: 'asc' | 'desc' | undefined): Item[] {
  return row.sort((a, b) => {
    if (sortDir === 'asc') {
      return a.UpdatedAt.getTime() - b.UpdatedAt.getTime();
    }

    return b.UpdatedAt.getTime() - a.UpdatedAt.getTime();
  });
}

function applyFilters(row: Item[], { query }: Filters): Item[] {
  return row.filter((item) => {
    if (query) {
      if (!item.Name?.toLowerCase().includes(query.toLowerCase())) {
        return false;
      }
    }

    return true;
  });
}
